import { PricingTypeEnum } from "../../graphql/types";
import { AccruedFee } from "./accrued-fee";
import { isNumber } from "./numbers";

export interface Price {
  amount: number;
}
export function isPrice(value: any): value is Price {
  const { amount } = value || {};
  return isNumber(amount);
}

export interface PriceRange {
  lower: number | null;
  upper: number | null;
}
export function isPriceRange(value: any): value is PriceRange {
  const { lower, upper } = value || {};
  return isNumber(lower) && isNumber(upper);
}

export function isSinglePrice(value: any): value is number {
  return isNumber(value);
}

export const formatPrice = (value: number | Price | PriceRange, currencySymbol: string) => {
  if (isNumber(value)) {
    const isNegative = value < 0;
    const amount = Math.abs(Math.round(value)).toLocaleString();
    return `${isNegative ? "-" : ""}${currencySymbol}${amount}`;
  } else if (isPrice(value)) {
    const isNegative = value.amount < 0;
    const amount = Math.abs(Math.round(value.amount)).toLocaleString();
    return `${isNegative ? "-" : ""}${currencySymbol}${amount}`;
  } else if (isPriceRange(value)) {
    const lower = Math.round(value.lower || 0);
    const upper = Math.round(value.upper || 0);
    const isLowerNegative = lower < 0;
    const isUpperNegative = upper < 0;
    return `${isLowerNegative ? "-" : ""}${currencySymbol}${Math.abs(lower).toLocaleString()} to ${
      isUpperNegative ? "-" : ""
    }${currencySymbol}${Math.abs(upper).toLocaleString()}`;
  }
  return `${currencySymbol}0`;
};

export const joinPricing = (
  values: (number | Price | PriceRange | undefined | null)[] | undefined
): number | PriceRange => {
  if (!values || values.length === 0) {
    return 0;
  }
  let lower = 0;
  let upper = 0;
  if (values.every(isNumber)) {
    const total = values.reduce((a, b) => a + b, 0);
    return total;
  }

  const singles = values.filter(isPrice);
  const ranges = values.filter(isPriceRange);
  const singleNumber = values.filter(isNumber);

  if (singles.length > 0) {
    lower += singles.reduce((a, b) => a + b.amount, 0);
    upper += singles.reduce((a, b) => a + b.amount, 0);
  }

  if (singleNumber.length > 0) {
    lower += singleNumber.reduce((a, b) => a + b, 0);
    upper += singleNumber.reduce((a, b) => a + b, 0);
  }

  if (ranges.length > 0) {
    lower += ranges.reduce((a, b) => a + (b?.lower || 0), 0);
    upper += ranges.reduce((a, b) => a + (b?.upper || 0), 0);
  } else {
    return lower;
  }

  return {
    lower,
    upper,
  };
};

export const formatMixedPricing = (
  values: (number | Price | PriceRange)[] | undefined,
  currencySymbol: string
): string => {
  if (!values || values.length === 0) {
    return `${currencySymbol}0`;
  }
  let lower = 0;
  let upper = 0;
  if (values.every(isNumber)) {
    const total = values.reduce((a, b) => a + b, 0);
    return `${currencySymbol}${Math.round(total).toLocaleString()}`;
  }

  const prices = values.filter(isPrice);
  const numbers = values.filter(isNumber);
  const ranges = values.filter(isPriceRange);

  if (prices.length > 0) {
    const priceSum = prices.reduce((a, b) => a + b.amount, 0);
    lower += priceSum;
    upper += priceSum;
  }

  if (numbers.length > 0) {
    const numberSum = numbers.reduce((a, b) => a + b, 0);
    lower += numberSum;
    upper += numberSum;
  }

  if (ranges.length > 0) {
    lower += ranges.reduce((a, b) => a + (b?.lower || 0), 0);
    upper += ranges.reduce((a, b) => a + (b?.upper || 0), 0);
  } else {
    return `${currencySymbol}${Math.round(lower).toLocaleString()}`;
  }

  return `${currencySymbol}${Math.round(lower).toLocaleString()} to ${currencySymbol}${Math.round(
    upper
  ).toLocaleString()}`;
};

export const formatPricing = (item: AccruedFee): string => {
  const mixedPricing = new Set(item.subMatters?.map((x) => x.pricingType)).size > 1;
  let label = "Price quoted - ";

  if (mixedPricing) {
    label += "Mixed Pricing Options";
    return label;
  }

  switch (item.pricingType) {
    case PricingTypeEnum.Fixed:
      label += "Fixed Fee";
      break;
    case PricingTypeEnum.EstimatedRange:
      label += "Estimate (Range)";
      break;
    case PricingTypeEnum.Estimated:
      label += "Estimate (Single figure)";
      break;
    default:
      label = "";
  }

  return label;
};
